// src/pages/GroupContextSelector.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Chip,
  Stack,
  Button,
  Avatar,
  Divider,
  Tooltip,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Groups";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import { fetchMyMemberships, activateMembership } from "../services/membershipService";
import { useAuth } from "../contexts/AuthContext";
import decodeJwtToken from "../auth/decodeJwtToken";
import { getPostAuthRoute } from "../shared/systemAccessRouting";
import {
  isNzengoGroup,
  supportsLoans,
  withResolvedGroupCategory,
  writeActiveGroupContext,
} from "../utils/groupCapabilities";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const currencyFmt = (value) =>
  `TZS ${new Intl.NumberFormat("en-TZ", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0))}`;

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

export default function GroupContextSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activatingId, setActivatingId] = useState(null);
  const cameFromLogin = Boolean(location.state?.fromLogin);

  useEffect(() => {
    let active = true;
    const loadMemberships = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchMyMemberships();
        if (active) {
          setMemberships((Array.isArray(data) ? data : []).map(withResolvedGroupCategory));
        }
      } catch (err) {
        if (active) {
          setError(err.message || "Unable to load your groups.");
          setMemberships([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    loadMemberships();
    return () => {
      active = false;
    };
  }, []);

  const handleEnterGroup = useCallback(
    async (membership) => {
      if (!membership) return;
      setActivatingId(membership.id);
      try {
        writeActiveGroupContext(membership);
        const payload = await activateMembership(membership.id);
        localStorage.setItem("accessToken", payload.access);
        localStorage.setItem("refreshToken", payload.refresh);
        refreshUser();
        const redirectTarget = getPostAuthRoute(
          payload?.access ? decodeJwtToken(payload.access) : null,
          { requireGroupSelection: false }
        );
        Swal.fire({
          icon: "success",
          title: "Context Updated",
          text: `You are now working inside ${membership.group_name}.`,
          confirmButtonColor: brand,
        }).then(() => {
          navigate(redirectTarget, { replace: true });
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Failed to switch group",
          text: err.message || "Please try again.",
          confirmButtonColor: brand,
        });
      } finally {
        setActivatingId(null);
      }
    },
    [navigate, refreshUser]
  );

  useEffect(() => {
    if (!loading && cameFromLogin && memberships.length === 1) {
      handleEnterGroup(memberships[0]);
    }
  }, [loading, cameFromLogin, memberships, handleEnterGroup]);

  const currentUserName = user?.full_name || user?.phone_number || "Member";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#faf8fb",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1080 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: brand }}>
              <GroupIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
                Select Active Group
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Hi {currentUserName}, you belong to{" "}
                <b>{memberships.length}</b> group{memberships.length === 1 ? "" : "s"}. Choose the workspace you want to open now.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Member can belong to multiple groups but uses one login. System context changes per selected group.">
              <IconButton size="small">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Small info card */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            boxShadow: 1,
            border: "1px dashed rgba(128, 11, 51, 0.2)",
            bgcolor: "#fff",
          }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="body2" color="text.secondary">
                Once you select a group, all dashboards, loans, contributions, dividends and reports will be filtered for that specific group only.
              </Typography>
              <Chip
                size="small"
                label="Multi-Group Member Flow"
                sx={{
                  bgcolor: "rgba(128, 11, 51, 0.08)",
                  color: brand,
                  fontWeight: 600,
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {loading && (
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={28} />
                </CardContent>
              </Card>
            </Grid>
          )}

          {!loading && memberships.length === 0 && (
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography color="text.secondary">
                    Hakuna Savvy Community vilivyopatikana kwenye akaunti yako. Tafadhali wasiliana na msimamizi ili aongeze kikundi kipya.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {memberships.map((membership, index) => (
            <Grid item xs={12} md={4} key={membership.id}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
                {(() => {
                  const membershipIsNzengo = isNzengoGroup(membership);
                  const membershipSupportsLoans = supportsLoans(membership);
                  return (
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: brand }}>
                        {membership.group_name
                          ?.split(" ")
                          .map((w) => w[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </Avatar>
                    }
                    title={
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                        {membership.group_name}
                      </Typography>
                    }
                    subheader={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <PersonOutlineOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Membership No: {membership.membership_no || "—"}
                        </Typography>
                      </Stack>
                    }
                    sx={{ pb: 0.5 }}
                  />

                  <CardContent sx={{ pt: 1.5, flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
                      <Chip
                        size="small"
                        label={membership.role || "Member"}
                        color="primary"
                        sx={{
                          bgcolor: "rgba(128, 11, 51, 0.08)",
                          color: brand,
                          fontWeight: 600,
                        }}
                      />
                      {membership.is_chairperson && (
                        <Chip size="small" label="Chairperson" color="success" variant="outlined" />
                      )}
                      {membership.is_secretary && (
                        <Chip size="small" label="Secretary" color="info" variant="outlined" />
                      )}
                      {membership.is_accountant && (
                        <Chip size="small" label="Accountant" color="warning" variant="outlined" />
                      )}
                      {membership.is_current && (
                        <Chip size="small" label="Current" color="success" />
                      )}
                      {membershipIsNzengo && (
                        <Chip size="small" label="Nzengo" color="secondary" />
                      )}
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      Status: {membership.status}
                    </Typography>

                    <Stack spacing={1.2} sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessCenterOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Group Account: {membership.group_account_number || "—"}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={2} sx={{ mt: 0.5, mb: 1 }}>
                        <Stack spacing={0}>
                          <Typography variant="caption" color="text.secondary">
                            Total Savings
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <SavingsOutlinedIcon fontSize="small" sx={{ color: brand }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {currencyFmt(membership.total_savings)}
                            </Typography>
                          </Stack>
                        </Stack>

                        {membershipSupportsLoans && (
                          <Stack spacing={0}>
                            <Typography variant="caption" color="text.secondary">
                              Loans Outstanding
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                              {currencyFmt(membership.total_loans_outstanding)}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" color="text.secondary">
                      {membershipIsNzengo
                        ? "When you enter this Nzengo workspace, you will see contribution and transfer workflows for this community unit."
                        : "When you enter this group workspace, you will see only loans, contributions, dividends and reports for this group."}
                    </Typography>
                  </CardContent>

                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      pt: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {membership.is_current ? "Currently active" : "Tap to switch"}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => handleEnterGroup(membership)}
                      sx={{
                        bgcolor: brand,
                        "&:hover": { bgcolor: brandHover },
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                      disabled={activatingId === membership.id}
                    >
                      {activatingId === membership.id ? "Setting up..." : "Enter Group Workspace"}
                    </Button>
                  </Box>
                </Card>
                  );
                })()}
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
